import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import AdminModal from "./AdminModal";

const AdminModalContext = createContext(null);

const defaultConfirmOptions = {
  title: "Are you sure?",
  description: "",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  tone: "default",
};

const defaultPromptOptions = {
  title: "Enter a value",
  label: "Name",
  placeholder: "",
  defaultValue: "",
  confirmLabel: "Save",
  cancelLabel: "Cancel",
  required: true,
};

export function AdminModalProvider({ children }) {
  const [modalState, setModalState] = useState(null);
  const [promptValue, setPromptValue] = useState("");
  const promptInputRef = useRef(null);

  const closeModal = useCallback((result) => {
    setModalState((current) => {
      current?.resolve?.(result);
      return null;
    });
    setPromptValue("");
  }, []);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setModalState({
        type: "confirm",
        resolve,
        ...defaultConfirmOptions,
        ...options,
      });
    });
  }, []);

  const prompt = useCallback((options = {}) => {
    return new Promise((resolve) => {
      const merged = { ...defaultPromptOptions, ...options };
      setPromptValue(merged.defaultValue ?? "");
      setModalState({
        type: "prompt",
        resolve,
        ...merged,
      });
    });
  }, []);

  useEffect(() => {
    if (modalState?.type === "prompt" && promptInputRef.current) {
      promptInputRef.current.focus();
      promptInputRef.current.select();
    }
  }, [modalState]);

  const isOpen = Boolean(modalState);
  const isConfirm = modalState?.type === "confirm";
  const isPrompt = modalState?.type === "prompt";
  const isDanger = modalState?.tone === "danger";

  const handlePromptSubmit = (event) => {
    event.preventDefault();
    const trimmed = promptValue.trim();
    if (modalState?.required && !trimmed) return;
    closeModal(trimmed || null);
  };

  return (
    <AdminModalContext.Provider value={{ confirm, prompt }}>
      {children}

      <AdminModal
        open={isOpen}
        onClose={() => closeModal(isConfirm ? false : null)}
        title={modalState?.title ?? ""}
        description={isConfirm ? modalState?.description : undefined}
        layout={isConfirm ? "confirm" : isPrompt ? "prompt" : undefined}
        showClose={!isConfirm}
        footer={
          isConfirm ? (
            <>
              <button
                type="button"
                className="admin-modal__btn admin-modal__btn--secondary"
                onClick={() => closeModal(false)}
              >
                {modalState?.cancelLabel}
              </button>
              <button
                type="button"
                className={`admin-modal__btn ${
                  isDanger
                    ? "admin-modal__btn--danger"
                    : "admin-modal__btn--primary"
                }`}
                onClick={() => closeModal(true)}
              >
                {modalState?.confirmLabel}
              </button>
            </>
          ) : isPrompt ? (
            <>
              <button
                type="button"
                className="admin-modal__btn admin-modal__btn--secondary"
                onClick={() => closeModal(null)}
              >
                {modalState?.cancelLabel}
              </button>
              <button
                type="submit"
                form="admin-modal-prompt-form"
                className="admin-modal__btn admin-modal__btn--primary"
              >
                {modalState?.confirmLabel}
              </button>
            </>
          ) : null
        }
      >
        {isPrompt ? (
          <form id="admin-modal-prompt-form" onSubmit={handlePromptSubmit}>
            <div className="admin-modal__field">
              <label htmlFor="admin-modal-prompt-input">{modalState.label}</label>
              <input
                ref={promptInputRef}
                id="admin-modal-prompt-input"
                type="text"
                value={promptValue}
                onChange={(event) => setPromptValue(event.target.value)}
                placeholder={modalState.placeholder}
                required={modalState.required}
              />
            </div>
          </form>
        ) : null}
      </AdminModal>
    </AdminModalContext.Provider>
  );
}

export function useAdminModal() {
  const context = useContext(AdminModalContext);
  if (!context) {
    throw new Error("useAdminModal must be used within AdminModalProvider");
  }
  return context;
}
